/* eslint-disable */
import * as d3Selection from 'd3-selection';
import * as d3Geo from 'd3-geo';
import * as d3Zoom from 'd3-zoom';
import View from './view';
import Utils from '../utils';

export default class MapView extends View {
    // model, d3, projection, dimensions, templates, asyncRender, interactive
    constructor(controller, container, config) {
        super(controller, container, config);

        // config
        this.model = this.config.model;
        this.modelForFocus = this.config.modelForFocus;
        this.dimensions = this.config.dimensions;
        this.projection = this.config.projection;
        this.dottedBlack = this.config.dottedBlack;
        this.dottedWhite = this.config.dottedWhite;
        this.interactive = this.config.interactive || true;
        this.scaleExtent = this.config.scaleExtent || [0.3, 60];
        this.animationLength = this.config.animationLength || 1000;
        this.asyncRender = this.config.asyncRender || false; // @TODO reimplement? - Rendering features asynchronously takes longer overall, but can improve long initial frame lengths
        this.templates = this.config.templates || {
            resetButton: 'Reset',
            fullScreenButton: 'Full Screen',
        };
        this.enableFullScreenTest = this.config.enableFullScreenTest || function () {
            const width = this.container.getBoundingClientRect().right - this.container.getBoundingClientRect().left;
            const isFullscreened = this.container.classList.contains('map--full-screen');
            // const test = this.enableFullScreenTest;
            return isFullscreened || width < 500; // && !Utils.inIframe();
        };
        this.canHover = this.config.canHover;
        this.hasOverlay = this.config.hasOverlay;
        this.resetButtonReference = d3Selection.select(this.container);

        // unconfig
        this.baseFeatureGroupClass = 'map-view__feature-group';
        this.baseFeatureClass = 'map-view__feature';

        // state
        this.featuresRendered = 0;
        this.interactionLocked = this.interactive || false;
        this.lastFeatureHovered = null;
        this.selectedFeature = null;

        this.zoom = d3Zoom.zoom()
            .scaleExtent(this.scaleExtent)
            .translateExtent([this.dimensions.map((n) => n * -0.4), this.dimensions.map((n) => n * 1.1)])
            .on('zoom', this.zoomed.bind(this));

        this.path = d3Geo.geoPath()
            .projection(this.projection);

        // this.getFeatureZoomTransform(this.model, this.scaleExtent[0]);
        // this.resetZoomTransform = () => {
        //     return this.getFeatureZoomTransform(this.model, 0.5);
        // }
    }

    featureHasData(feature, key) {
        return typeof feature.properties.values[key] !== undefined && feature.properties.values[key] !== null;
    }

    removeNode() {
        if (this.svg) {
            this.svg.node().remove();
        }
    }

    destroyChildren() {
        if (this.group) {
            Utils.destroyChildren(this.group);
        }
    }

    renderContainers() {

        this.removeNode();

        this.container.style.height = '100%';
        this.container.style.width = '100%';

        this.svg = d3Selection.select(this.container)
            .append('svg')
            .attr('class', 'map__map-view')
            .attr('role', 'presentation')
            .attr('preserveAspectRatio', 'xMidYMid meet')
            // .attr('viewBox', `0 0 ${this.dimensions[0]} ${this.dimensions[1]}`)
            .attr('draggable', 'false')
            .call(this.zoom);

        this.group = this.svg.append('g')
            .attr('class', this.baseFeatureGroupClass);

        this.dotted = this.svg.append('g')
            .attr('class', 'dotted');
    }

    render() {
        // this.destroyFeatures();
        this.renderContainers();
        this.renderFeatures();
        this.renderDotted(this.dottedBlack, 'dotted_line--black');
        this.renderDotted(this.dottedWhite, 'dotted_line--white');
        this.renderButtons();
        this.controller.addTopicHandler('reset-map', this.onResetMap.bind(this));
        this.controller.addTopicHandler('enter-full-screen', () => {
            this.container.classList.add('map--full-screen');
            this.emit('reset-map', {
                animationDuration: 0,
            });
        });
        this.controller.addTopicHandler('exit-full-screen', () => {
            this.container.classList.remove('map--full-screen');
            this.emit('reset-map', {
                animationDuration: 0,
            });
        });
        this.controller.addTopicHandler('focus-feature', this.onFocusFeature.bind(this));
        this.controller.addTopicHandler('container-resize', this.onContainerResize.bind(this));
        this.emit('reset-map', {
            animationDuration: 0,
        });
        this.pauseInteractivity();
        this.svg.on('click', () => {
            this.unpauseInteractivity();
        });

        // MS Edge & IE 11, 10 etc don't support vector-effect
        // temporary workaround
        if (this.group.node().style.vectorEffect === undefined) {
            this.container.classList.add('map-vector-effect-not-supported');
        }
    }

    renderData(key, colorScale, callback) {
        const features = this.model.features;
        const dataValueKey = key;
        const colour = colorScale;

        this.group.selectAll(`.${this.baseFeatureClass}`)
            .classed(`${this.baseFeatureClass}--no-data`, (d) => {
                return !this.featureHasData(d, key);
            })
            .transition()
            .duration(250)
            .attr('fill', (d) => colour(d.properties.values[dataValueKey]));

        if (callback) {
            callback();
        }
    }

    renderFeatures(callback) {
        this.destroyChildren();

        const features = this.model.features;
        this.selectionOfPaths = this.group.selectAll(`.${this.baseFeatureClass}`)
            .data(features)
            .enter()
            .append('path')
            .attr('d', this.path)
            .attr('class', (d) => this.getFeatureClassList(d).join(' '))
            .attr('vector-effect', 'non-scaling-stroke');

        this.selectionOfPaths.on('click', (d, i, s) => {
            if (!d.properties.values.disableTooltip && d.id !== 'false') {
                this.handleFeatureClick(d, i, s);
                this.selectedFeature = s[i];
            } else {
                this.emit('hide-tooltip', {});
            }
        });

        if (this.canHover) {
            this.selectionOfPaths.on('mouseover', (d, i, s) => {
                const node = s[i];
                const id = d.id;

                if(this.lastFeatureHovered !== id) {
                    this.handleFeatureHoverOn(d, i, s);
                    const currentClass =  `map-view__feature--active ${d3Selection.select(node).attr('class')}`;
                    d3Selection.selectAll('.map-view__feature--active').classed('map-view__feature--active', false);
                    d3Selection.select(node).attr('class', currentClass);
                    this.lastFeatureHovered = id;
                }
            });

            this.selectionOfPaths.on('mouseout', (d, i, s) => {
                const node = s[i];
                this.handleFeatureHoverOff(d, i, s);
                d3Selection.select(node).classed('map-view__feature--active', false);
            });
        }

        this.selectionOfPaths.on('mousemove', (d, i, s) => {
            const position = d3Selection.mouse(this.container);
            this.handleFeatureMouseMove(position);
        });

        this.selectionOfPaths.on('mouseleave', (d, i, s) => {
            d3Selection.select('.map__tooltip-view').style('opacity', 0);
        });

        if (callback) {
            callback();
        }

        // for (let i = 0; i < features.length; i++) {
        //     const feature = features[i];

        //     if (this.asyncRender) {
        //         setTimeout(() => {
        //             this.renderFeature(feature, callback);
        //         });
        //     }
        //     else {
        //         this.renderFeature(feature, callback);
        //     }
        // }
    }

    renderDotted(model, classname, callback) {
        const features = model.features;
        this.selectionOfPaths = this.dotted.selectAll(`.${classname}`)
            .data(features)
            .enter()
            .append('path')
            .attr('class', `dotted_line ${classname}`)
            .attr('d', this.path)
            .style('fill', 'none');

        if (callback) {
            callback();
        }
    }

    // renderFeature(feature, callback) {
    //     const datum = feature.properties.values[this.dataValueKey];
    //     const className = this.getFeatureClassName(feature.id, datum);
    //     const fillColour = this.getFeatureFillFromValue(datum);

    //     const selection = this.group.append('path')
    //         .datum(feature)
    //         .attr('class', className)
    //         .attr('d', this.path)
    //         .attr('vector-effect', 'non-scaling-stroke')
    //         .attr('fill', fillColour)
    //         .style('opacity', 0);

    //     selection.transition().duration(250).style('opacity', 1);

    //     this.featuresRendered++;
    //     this.renderCallbackCaller(callback);
    // }

    handleFeatureMouseMove(position) {
        this.emit('mousemove-feature', {
            position: position,
        });
    }

    handleFeatureClick(d, i, s) {
        const feature = d;
        const node = s[i];
        const parentEl = node.parentNode;

        parentEl.removeChild(node);
        parentEl.appendChild(node);

        this.emitFeatureFocus(feature);
    }

    handleFeatureHoverOn(d, i, s) {
        const datum = d;
        const node = s[i];

        this.hoverFeatureToggle(datum, node);
        this.emit('hover-feature', {
            feature: datum,
            node: node,
        });
    }

    handleFeatureHoverOff(d, i, s) {
        const datum = d;
        const node = s[i];

        this.hoverFeatureToggle(datum, node);
        this.emit('unhover-feature', {
            feature: datum,
            node: node,
        });
    }

    emitFeatureFocus(feature, viewportFillFactor, duration) {
        const fillFactor = viewportFillFactor || null;

        this.emit('focus-feature', {
            feature: feature,
            viewportFillFactor: fillFactor,
            animationLength: duration,
        });
    }

    hoverFeatureToggle(feature, node) {
        node.parentNode.appendChild(node);

        // Keep any currently focused feature on top
        this.group.selectAll(`.${this.baseFeatureClass}--focussed`)
            .raise();
    }

    zoomed() {
        const event = d3Selection.event;
        const transform = this.getTransform().translate(event.transform.x, event.transform.y).scale(event.transform.k);
        if (isNaN(event.transform.x) || isNaN(event.transform.x) || isNaN(event.transform.x)) {
            console.warn('Invalid transform.', transform);
        }
        else {
            this.setZoom(transform);
            this.emit('zoom-change', {
                transform: transform,
            });
        }
    }

    getFeatureClassList(feature) {
        const classes = [];
        const idName = (feature && feature.id) ? feature.id.toLowerCase() : 'no-id';

        classes.push(this.baseFeatureClass);
        classes.push(`${this.baseFeatureClass}--${idName}`);

        return classes;
    }

    getFeatureFillFromValue(value) {
        return value ? '#ffcccc' : '#000000';
    }

    focusFeature(feature, viewportFillFactor, animationLength) {
        const duration = animationLength !== 0 && !animationLength ? this.animationLength : animationLength;

        if (feature && feature.type && (feature.type === 'FeatureCollection' || feature.type === 'Feature')) {
            this.group.selectAll(`.${this.baseFeatureClass}`)
                .classed(`${this.baseFeatureClass}--focussed`, false);

            this.group.selectAll(`.${this.baseFeatureClass}`)
                .filter(d => feature.id === d.id)
                .classed(`${this.baseFeatureClass}--focussed`, true)
                .raise();
        }
        else {
            throw new Error('Expected a GeoJSON Feature or FeatureCollection');
        }

        this.zoomToFeature(feature, viewportFillFactor, duration);
    }

    onResetMap(event) {
        const duration = event && (event.animationDuration || event.animationDuration === 0) ? event.animationDuration : this.animationLength;
        this.focusFeature(this.modelForFocus, 0.9, duration);
        this.selectedFeature = null;
        this.showResetButton(true);
    }

    onFocusFeature(event) {
        if (event.feature.id !== null) {
            this.focusFeature(event.feature, event.viewportFillFactor, event.animationLength);
            this.showResetButton(false);
        }
    }

    showResetButton(bool) {
        this.resetButtonReference
            .select('.map-view__button--zoom-reset')
            .classed('map-view__button--zoom-reset-hidden', bool);
    }

    getFeatureZoomTransform(feature, viewportFillFactor) {
        const scaleMultiplier = viewportFillFactor || 0.65;
        const bounds = this.path.bounds(feature);
        const rect = this.svg.node().getBoundingClientRect();
        const width = rect.right - rect.left; // this.dimensions[0];
        const height = rect.bottom - rect.top; // this.dimensions[1];
        const dx = bounds[1][0] - bounds[0][0];
        const dy = bounds[1][1] - bounds[0][1];
        const x = (bounds[0][0] + bounds[1][0]) / 2;
        const y = (bounds[0][1] + bounds[1][1]) / 2;
        const scale = Math.max(this.scaleExtent[0], Math.min(this.scaleExtent[1], scaleMultiplier / Math.max(dx / width, dy / height)));
        const translate = [(width / 2) - (scale * x), (height / 2) - (scale * y)];
        const transform = {
            x: translate[0],
            y: translate[1],
            k: scale,
        };

        return transform;
    }

    zoomToFeature(feature, viewportFillFactor, duration) {
        const transform = this.getFeatureZoomTransform(feature, viewportFillFactor);
        this.zoomToTransform(transform, duration);
    }

    zoomToTransform(transform, duration) {
        const transition = this.svg.transition().duration(duration);
        const transformFunction = d3Zoom.zoomIdentity.translate(transform.x, transform.y).scale(transform.k);

        this.zoom.transform(transition, transformFunction);
    }

    zoomBy(factor, duration) {
        const transition = this.svg.transition().duration(duration);
        this.zoom.scaleBy(transition, factor);
        this.showResetButton(false);
    }

    zoomTo(scale, duration) {
        const transition = this.svg.transition().duration(duration);
        this.zoom.scaleTo(transition, scale);
    }

    pauseInteractivity() {
        if (this.svg) {
            this.svg.classed('map__map-view--paused', true);
            this.svg.on('.zoom', null);
        }
    }

    unpauseInteractivity() {
        if (this.svg) {
            this.svg.classed('map__map-view--paused', false);
            this.svg.call(this.zoom).on('wheel.zoom', null).on('dblclick.zoom', null);
        }
    }

    resetZoom(animationDuration) {
        const duration = animationDuration || 0;
        this.zoomToTransform(this.resetZoomTransform(), duration);
    }

    setZoom(transform, duration) {
        this.setTransform(transform, duration);
    }

    setTransform(targetTransform) {
        this.group.attr('transform', targetTransform);
        this.dotted.attr('transform', targetTransform);
    }

    getTransform() {
        return d3Zoom.zoomTransform(this.zoom);
    }

    renderFullScreenButton() {
        const fullScreenButton = this.createButton(this.emit.bind(this, 'enter-full-screen'));
        fullScreenButton.innerHTML = this.templates.fullScreenButton;
        fullScreenButton.classList.add('map-view__button--full-screen');

        this.fullScreenButton = fullScreenButton;
        this.container.appendChild(fullScreenButton);
    }

    onContainerResize() {
        this.emit('reset-map', { animationDuration: this.animationLength });
        this.toggleButton(this.fullScreenButton, this.enableFullScreenTest());
    }

    toggleButton(button, isDisplayed) {
        if (!button) {
            return;
        }

        if (isDisplayed) {
            this.fullScreenButton.style.display = 'block';
        }
        else {
            this.fullScreenButton.style.display = 'none';
        }
    }

    renderButtons() {
        if(this.canHover || !this.hasOverlay) {
            this.renderResetButton();
            // this.renderFullScreenButton();
        }
    }

    renderResetButton() {
        const resetButton = this.createButton(this.emit.bind(this, 'reset-map', {
            animationDuration: 1000,
        }));
        resetButton.innerHTML = __('map-reset');
        resetButton.className += ' map-view__button--zoom-reset map-view__button--zoom-reset-hidden';

        this.container.appendChild(resetButton);
    }

    createButton(callback) {
        const button = document.createElement('button');

        button.textContent = 'zoom';
        button.className = 'map-view__button';
        button.addEventListener('click', callback);

        return button;
    }

}
